// import { Typography } from "@mui/material";
// import React, { useEffect, useState } from "react";

// const UserLogDashboard = () => {
//   const [, setDashboardUrl] = useState(null);
//   const BASEURL = process.env.REACT_APP_BASE_URL;

//   useEffect(() => {
//     const fetchDashboardUrl = async () => {
//       try {
//         const response = await fetch(`${BASEURL}api/dashboard-url`);
//         const data = await response.json();
//         setDashboardUrl(data.url);
//       } catch (err) {
//         console.error("Failed to load dashboard URL:", err);
//       }
//     };

//     fetchDashboardUrl();
//   }, [BASEURL]);

//   return (
//     <div>
//       <Typography variant="h4" gutterBottom>
//         User Logs Dashboard
//       </Typography>
//       <h4>User logs will appear here</h4>
//       {/* {dashboardUrl ? (
//               <iframe
//                 src={dashboardUrl}
//                 title="Grafana Dashboard"
//                 width="100%"
//                 height="500"
//                 frameBorder ="0"
//               />

//         // ></iframe>
//       ) : (
//         <p>Loading dashboard...</p>
//       )} */}
//     </div>
//   );
// };

// export default UserLogDashboard;
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridLoader from "../components/AgGridLoader";
import { getHeaderClass } from "../utils/agGridHelpers";
import axiosInstance from "../utils/axiosInstance";
import { useSelector } from "react-redux";

// ── Level chip cell renderer ─────────────────────────────────────────────────
const LEVEL_COLOR = { info: "info", warn: "warning", error: "error" };

const LevelCellRenderer = (params) => {
  const lvl = (params.value || "").toLowerCase();
  return (
    <Chip
      label={lvl.toUpperCase()}
      color={LEVEL_COLOR[lvl] || "default"}
      size="small"
      sx={{ fontWeight: 600, minWidth: 56 }}
    />
  );
};

// ── Role chip cell renderer ──────────────────────────────────────────────────
const RoleCellRenderer = (params) => {
  if (!params.value) return <span style={{ color: "#bbb" }}>—</span>;
  return (
    <Chip
      label={params.value}
      size="small"
      variant="outlined"
      color={params.value === "superadmin" ? "secondary" : "default"}
      sx={{ fontSize: "0.72rem" }}
    />
  );
};

// ── Row background by level ──────────────────────────────────────────────────
const getRowStyle = (params) => {
  const lvl = (params.data?.level || "").toLowerCase();
  if (lvl === "error") return { backgroundColor: "#fff5f5" };
  if (lvl === "warn")  return { backgroundColor: "#fffde7" };
  return null;
};

// ── Main component ───────────────────────────────────────────────────────────
const UserLogDashboard = () => {
  const { user } = useSelector((s) => s.auth);
  const gridRef = useRef(null);   // AgGridReact component ref
  const apiRef  = useRef(null);   // grid API ref (set in onGridReady)

  const [rowData, setRowData]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [total, setTotal]         = useState(0);

  // Server-side filters — default to today so the viewer opens on today's file
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom]   = useState(todayStr);
  const [dateTo, setDateTo]       = useState(todayStr);
  // Client-side quick filter
  const [quickFilter, setQuickFilter] = useState("");
  // Level toggle (applied via AG Grid external filter)
  const [level, setLevel]         = useState("all");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Load all entries for chosen date range (backend handles role filtering).
      // AG Grid handles level/search client-side.
      const params = { limit: 2000, page: 1, dateFrom, dateTo };

      const res = await axiosInstance.get("/api/logs/view", { params });
      const entries = (res.data?.entries || []).map((e) => ({
        timestamp: e.timestamp,
        level:     e.level,
        message:   e.message || "",
        page:      e.meta?.page || "",
        username:  e.meta?.username || "",
        role:      e.meta?.role || "",
        ip:        e.meta?.ip || "",
        location:  e.meta?.location || "",
      }));
      setRowData(entries);
      setTotal(entries.length);
    } catch {
      setError("Failed to load logs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // External level filter fed into AG Grid
  useEffect(() => {
    apiRef.current?.onFilterChanged();
  }, [level]);

  const isExternalFilterPresent = useCallback(() => level !== "all", [level]);

  const doesExternalFilterPass = useCallback(
    (node) => {
      if (level === "all") return true;
      return (node.data?.level || "").toLowerCase() === level;
    },
    [level]
  );

  const onGridReady = (params) => {
    apiRef.current = params.api;
  };

  const columnDefs = useMemo(() => [
    {
      headerName: "Timestamp",
      field: "timestamp",
      width: 160,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellStyle: { fontSize: "0.78rem", color: "#555" },
    },
    {
      headerName: "Level",
      field: "level",
      width: 90,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellRenderer: LevelCellRenderer,
    },
    {
      headerName: "Message",
      field: "message",
      flex: 2,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellStyle: { fontSize: "0.82rem" },
      tooltipField: "message",
    },
    {
      headerName: "Page",
      field: "page",
      width: 160,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellStyle: { fontSize: "0.78rem", color: "#666" },
    },
    {
      headerName: "User",
      field: "username",
      width: 120,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellStyle: { fontWeight: 500, fontSize: "0.82rem" },
    },
    {
      headerName: "Role",
      field: "role",
      width: 110,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellRenderer: RoleCellRenderer,
    },
    {
      headerName: "IP",
      field: "ip",
      width: 115,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellStyle: { fontSize: "0.78rem", color: "#777" },
    },
    {
      headerName: "Location",
      field: "location",
      width: 120,
      sortable: true,
      filter: "agTextColumnFilter",
      headerClass: getHeaderClass,
      cellStyle: { fontSize: "0.78rem", color: "#777" },
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    headerClass: getHeaderClass,
  }), []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 130px)" }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Activity Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.role === "superadmin"
              ? "Showing all user activity across every role."
              : "Showing all user activity. Superadmin actions are not visible to your role."}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={fetchLogs} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5, alignItems: "center" }}>
        {/* Level toggle */}
        <ToggleButtonGroup
          value={level}
          exclusive
          size="small"
          onChange={(_, v) => { if (v !== null) setLevel(v); }}
        >
          {[
            { val: "all",   label: "All" },
            { val: "info",  label: "INFO" },
            { val: "warn",  label: "WARN" },
            { val: "error", label: "ERROR" },
          ].map(({ val, label }) => (
            <ToggleButton key={val} value={val} sx={{ textTransform: "none", px: 1.5 }}>
              {val === "all" ? "All" : (
                <Chip
                  label={label}
                  color={LEVEL_COLOR[val]}
                  size="small"
                  sx={{ fontWeight: 600, pointerEvents: "none" }}
                />
              )}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Date range — reloads from server */}
        <TextField
          type="date"
          size="small"
          label="From"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          inputProps={{ max: dateTo || undefined }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: dateFrom ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setDateFrom("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ width: 170 }}
        />
        <TextField
          type="date"
          size="small"
          label="To"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          inputProps={{ min: dateFrom || undefined }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: dateTo ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setDateTo("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ width: 170 }}
        />

        {/* Quick filter — AG Grid client-side */}
        <TextField
          size="small"
          placeholder="Search message, user, role…"
          value={quickFilter}
          onChange={(e) => setQuickFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: quickFilter ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQuickFilter("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ width: 260 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {loading ? "Loading…" : `${total.toLocaleString()} entries`}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {/* ── AG Grid ── */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            quickFilterText={quickFilter}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            getRowStyle={getRowStyle}
            pagination
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100, 200]}
            onGridReady={onGridReady}
            loadingOverlayComponent={AgGridLoader}
            loadingOverlayComponentParams={{ loadingMessage: "Loading logs…" }}
            loading={loading}
            suppressMovableColumns
            tooltipShowDelay={300}
          />
        </div>
      </Box>
    </Box>
  );
};

export default UserLogDashboard;
